/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  MouseEvent,
  ReactNode
} from 'react';
import {makeStyles} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {ButtonSpinner} from '../loading/ButtonSpinner';

export interface CellButtonMenuItemType {
  onClick: (e: MouseEvent<HTMLLIElement>) => void;
  icon?: ReactNode;
  label: string;
  disabled?: boolean;
  isLoading?: boolean;
  title?: string;
  customStyle?: string;
  direction?: 'row' | 'column';
}

const useStyle = makeStyles(() => ({
  labelContainer : {
    position       : 'relative',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  loader : {
    position  : 'absolute',
    margin    : 0,
    marginTop : '10px'
  }
}));

/**
 * @CellMenuItem
 *
 * CellMenuItem component showing inside CellMenu
 *
 *    <CellMenuItem icon={<MapEditIcon />}
 *      onClick={editAsset}
 *      label={translate('admin-assets:grid.menu.editAsset')} />
 *    <CellMenuItem icon={<SystemDeleteIcon />}
 *      onClick={deleteAsset}
 *      label={translate('admin-assets:grid.menu.delete')} />
 *
 */
export const CellMenuItem = forwardRef<HTMLLIElement, CellButtonMenuItemType>(({
  onClick,
  icon,
  disabled,
  label,
  isLoading,
  title,
  customStyle,
  direction = 'row'
}: CellButtonMenuItemType, ref) => {
  const isPhone = useIsPhone();
  const classes = useStyle();
  return (
    <Tooltip arrow placement={'bottom'}
      disableHoverListener={!title}
      title={<span>{title}</span>}>
      <MenuItem ref={ref}
        onClick={onClick}
        className={customStyle}
        disabled={disabled || isLoading}>
        <Box display='flex' flexDirection={direction} p={1}>
          {
            icon && (
              <Box display='flex' ml={isPhone ? 2 : 0} alignItems='center'>
                {icon}
              </Box>
            )
          }
          <Box display='flex' ml={1} alignItems='center'>
            <div className={classes.labelContainer}>
              {isLoading && <ButtonSpinner overrideClassName={classes.loader} />}
              {label}
            </div>
          </Box>
        </Box>
      </MenuItem>
    </Tooltip>
  );
});
